const SHARE_STATUS_SHARED = 0;
const SHARE_STATUS_DOWNLOAD_STARTED = 1;
const SHARE_STATUS_CANCELLED = 2;
const SHARE_STATUS_FAILED = 3;
const SHARE_STATUS_COPIED = 4;

/**
 * @param {string} url
 * @returns {Promise<number>}
 */
export async function shareLinkWeb(url) {
    if (typeof navigator.share === "function") {
        try {
            await navigator.share({ url });
            return SHARE_STATUS_SHARED;
        } catch (error) {
            if (error?.name === "AbortError") return SHARE_STATUS_CANCELLED;
        }
    }

    try {
        if (!navigator.clipboard?.writeText) return SHARE_STATUS_FAILED;
        await navigator.clipboard.writeText(url);
        return SHARE_STATUS_COPIED;
    } catch (_) {
        return SHARE_STATUS_FAILED;
    }
}

/**
 * @param {string} url
 * @param {string|null} text
 * @returns {Promise<number>}
 */
export async function sharePhotoWeb(url, text) {
    try {
        const response = await fetch(url);
        if (!response.ok) return SHARE_STATUS_FAILED;

        const blob = await response.blob();
        const mimeType = blob.type && blob.type.startsWith("image/")
            ? blob.type
            : mimeTypeForUrl(url);
        const extension = extensionForMimeType(mimeType);
        const file = new File([blob], `photo.${extension}`, { type: mimeType });
        const shareData = text
            ? { files: [file], text }
            : { files: [file] };
        const canShareFile = navigator.share &&
            (!navigator.canShare || navigator.canShare(shareData));

        if (canShareFile) {
            try {
                await navigator.share(shareData);
                return SHARE_STATUS_SHARED;
            } catch (error) {
                if (error?.name === "AbortError") return SHARE_STATUS_CANCELLED;
            }
        }

        downloadPhoto(blob, extension);
        return SHARE_STATUS_DOWNLOAD_STARTED;
    } catch (_) {
        return SHARE_STATUS_FAILED;
    }
}

/**
 * @param {Uint8Array} bytes
 * @param {string} extension
 * @param {string|null} text
 * @returns {Promise<number>}
 */
export async function sharePhotoBytesWeb(bytes, extension, text) {
    try {
        const normalizedExtension = normalizeExtension(extension);
        const file = createPhotoFile(bytes, normalizedExtension);
        const shareData = text
            ? { files: [file], text }
            : { files: [file] };
        if (canShareData(shareData)) {
            try {
                await navigator.share(shareData);
                return SHARE_STATUS_SHARED;
            } catch (error) {
                if (error?.name === "AbortError") return SHARE_STATUS_CANCELLED;
            }
        }

        downloadPhoto(file, normalizedExtension);
        return SHARE_STATUS_DOWNLOAD_STARTED;
    } catch (_) {
        return SHARE_STATUS_FAILED;
    }
}

/**
 * @param {Uint8Array} bytes
 * @param {string} extension
 * @returns {boolean}
 */
export function canSharePhotoFileWeb(bytes, extension) {
    try {
        return canShareData({ files: [createPhotoFile(bytes, extension)] });
    } catch (_) {
        return false;
    }
}

/**
 * @param {Blob} blob
 * @param {string} extension
 */
function downloadPhoto(blob, extension) {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = `photo.${extension}`;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    try {
        anchor.click();
    } finally {
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
}

/**
 * @param {string} value
 * @returns {string}
 */
function mimeTypeForUrl(value) {
    const path = value.split("?")[0].split("#")[0].toLowerCase();
    if (path.endsWith(".png")) return "image/png";
    if (path.endsWith(".webp")) return "image/webp";
    return "image/jpeg";
}

/**
 * @param {string} mimeType
 * @returns {string}
 */
function extensionForMimeType(mimeType) {
    if (mimeType === "image/png") return "png";
    if (mimeType === "image/webp") return "webp";
    return "jpg";
}

/**
 * @param {{files: File[], text?: string}} shareData
 * @returns {boolean}
 */
function canShareData(shareData) {
    return typeof navigator.share === "function" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare(shareData);
}

/**
 * @param {Uint8Array} bytes
 * @param {string} extension
 * @returns {File}
 */
function createPhotoFile(bytes, extension) {
    const normalizedExtension = normalizeExtension(extension);
    return new File(
        [bytes],
        `photo.${normalizedExtension}`,
        { type: mimeTypeForExtension(normalizedExtension) },
    );
}

/**
 * @param {string} extension
 * @returns {string}
 */
function normalizeExtension(extension) {
    const normalized = extension.toLowerCase().replace(/^\./, "");
    if (normalized === "png" || normalized === "webp") return normalized;
    return "jpg";
}

/**
 * @param {string} extension
 * @returns {string}
 */
function mimeTypeForExtension(extension) {
    if (extension === "png") return "image/png";
    if (extension === "webp") return "image/webp";
    return "image/jpeg";
}
